import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const LINKS = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'clients', label: 'Clients' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'contact', label: 'Contact' },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const handleClick = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <header
            data-testid={NAV.root}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? 'bg-black/85 backdrop-blur-xl border-b border-white/10 py-3'
                    : 'bg-transparent py-6'
            }`}
        >
            <div className="container-lux flex items-center justify-between">
                <button
                    data-testid={NAV.logo}
                    onClick={() => handleClick('home')}
                    className="flex items-center gap-3 group"
                >
                    <img
                        src="https://customer-assets.emergentagent.com/job_experience-craft-8/artifacts/ylt1lu9g_IMG_8022.png"
                        alt="Mindstorm Productions"
                        className="h-20 sm:h-24 md:h-28 w-auto object-contain"
                        style={{ filter: 'invert(1) brightness(1.1) contrast(1.05)' }}
                    />
                </button>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map(l => (
                        <button
                            key={l.id}
                            data-testid={NAV.link(l.id)}
                            onClick={() => handleClick(l.id)}
                            className="text-[11px] tracking-[0.25em] uppercase text-white/70 hover:text-gold transition-colors font-medium"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <button
                        data-testid={NAV.ctaContact}
                        onClick={() => handleClick('contact')}
                        className="btn-primary text-[10px] py-3 px-6"
                    >
                        Start Your Project
                    </button>
                </div>

                <button
                    data-testid={NAV.mobileToggle}
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-white p-2"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid={NAV.mobileMenu}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
                    >
                        <div className="container-lux py-6 flex flex-col gap-5">
                            {LINKS.map(l => (
                                <button
                                    key={l.id}
                                    data-testid={NAV.link(`m-${l.id}`)}
                                    onClick={() => handleClick(l.id)}
                                    className="text-left text-white/80 hover:text-gold text-[12px] tracking-[0.25em] uppercase"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                onClick={() => handleClick('contact')}
                                className="btn-primary mt-2 self-start"
                            >
                                Start Your Project
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Nav;
